<script setup lang="ts">
// Page publique « Histoire des fléchettes en France » (/histoire), DA du calendrier
// DartsHub (Compet, thèmes calendrier-light / calendrier-dark). Données : API /history
// (frise, palmarès, doyens, découpage fédéral) et src/data/history.ts (JO, estimations).
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dartsHubApi from '@/axios/dartsHubApi';
import logoLight from '@/assets/logo-bleu.png';
import logoDark from '@/assets/logo.png';
import { CLUBS_PER_SEASON, JO_CREATIONS, TOP_DEPARTMENTS, TREES } from '@/data/history';

interface HistoryEvent { id: number; date: string; kind: string; region: string; title: string; detail: string | null }
interface Competition { id: number; slug: string; title: string; note: string | null; columns: string[]; rows: (string | null)[][] }
interface Doyen { name: string; logo: string | null; comite: string | null; commune: string | null; founded_on: string; founded_note: string | null }
interface FederalMap { seasons: Record<string, Record<string, Record<string, number>>>; ligue_of: Record<string, Record<string, string>>; comites: Record<string, string>; ligues: Record<string, string> }
interface ClubHist { date: string; kind: string; text: string }
interface ClubItem { name: string; commune: string | null; comite: string; region: string; status: 'actif' | 'disparu' | 'dissous'; dissolved: string | null; founded: string | null; founded_note: string | null; first: string | null; last: string | null; archives: boolean; history: ClubHist[]; note: string | null }
interface JoMap { paths: [string, string, string][]; cum: Record<string, Record<string, number>> }

const VIEWS = [
  { id: 'frise', label: 'Frise' },
  { id: 'carte', label: 'Carte' },
  { id: 'arbres', label: 'Arbres des Comités' },
  { id: 'doyens', label: 'Doyens' },
  { id: 'clubs', label: 'Les clubs' },
  { id: 'palmares', label: 'Palmarès' },
  { id: 'chiffres', label: 'En chiffres' },
  { id: 'contribuer', label: 'Contribuer' },
];
const route = useRoute();
const router = useRouter();
const view = computed(() => {
  const h = route.hash.slice(1);
  return VIEWS.some(v => v.id === h) ? h : 'frise';
});
const go = (id: string) => router.replace({ hash: `#${id}` });

const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const KIND_LABEL: Record<string, string> = { federation: 'Fédération', ligue: 'Ligue', comite: 'Comité', club: 'Club' };
const REGIONS: [string, string][] = [
  ['all', 'Toute la France'], ['sud-est', 'Sud-Est'], ['aquitaine', 'Aquitaine'], ['bretagne', 'Bretagne'],
  ['pays-de-loire', 'Pays de la Loire'], ['nord', 'Nord et Île-de-France'], ['est', 'Est et Bourgogne'],
  ['sud-ouest', 'Sud-Ouest'], ['outre-mer', 'Outre-mer'],
];

// --- Frise
const events = ref<HistoryEvent[]>([]);
const kindFilter = ref('all');
const regionFilter = ref('all');
const groupedEvents = computed(() => {
  const groups = new Map<number, HistoryEvent[]>();
  for (const e of events.value) {
    if (kindFilter.value !== 'all' && e.kind !== kindFilter.value) continue;
    if (regionFilter.value !== 'all' && e.region !== regionFilter.value) continue;
    const d = Math.floor(Number(e.date.slice(0, 4)) / 10) * 10;
    if (!groups.has(d)) groups.set(d, []);
    groups.get(d)!.push(e);
  }
  return [...groups.entries()];
});

// --- Doyens
const doyens = ref<Doyen[]>([]);
const initials = (n: string) => n.replace(/\b(Darts?|Club|DC|Les?|La|De|Du|Des|The)\b/gi, '').trim()
  .split(/[\s'-]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
const foundedLabel = (d: Doyen) => {
  const [y, m, day] = d.founded_on.split('-');
  return d.founded_note ? `Créé en ${y} ${d.founded_note}` : `Créé le ${Number(day)} ${MONTHS[Number(m) - 1]} ${y}`;
};
const logoUrl = (logo: string) => (logo.startsWith('http') ? logo : `${dartsHubApi.defaults.baseURL}${logo}`);

// --- Les clubs (actifs, disparus, dissous) par Comité
const clubs = ref<ClubItem[]>([]);
const clubRegion = ref('all');
const clubComite = ref('all');
const clubStatus = ref('all');
const STATUS_LABEL: Record<string, string> = { actif: 'Affilié', disparu: 'Non affilié', dissous: 'Dissous' };
const clubComites = computed(() => [...new Set(clubs.value
  .filter(c => clubRegion.value === 'all' || c.region === clubRegion.value).map(c => c.comite))].sort());
watch(clubRegion, () => { clubComite.value = 'all'; });
const clubGroups = computed(() => {
  const groups = new Map<string, ClubItem[]>();
  for (const c of clubs.value) {
    if (clubRegion.value !== 'all' && c.region !== clubRegion.value) continue;
    if (clubComite.value !== 'all' && c.comite !== clubComite.value) continue;
    if (clubStatus.value === 'actif' && c.status !== 'actif') continue;
    if (clubStatus.value === 'ancien' && c.status === 'actif') continue;
    if (!groups.has(c.comite)) groups.set(c.comite, []);
    groups.get(c.comite)!.push(c);
  }
  // Par ancienneté : date de création de l'association, à défaut première saison ou année
  // où le club apparaît ; les clubs sans aucune date en dernier.
  const age = (c: ClubItem) => c.founded || (c.first ? `${c.first.slice(0, 4)}-12-31` : '9999');
  for (const list of groups.values()) list.sort((a, b) => age(a).localeCompare(age(b)) || a.name.localeCompare(b.name));
  return [...groups.entries()];
});
const fmtDate = (d: string) => {
  const [y, m, day] = d.split('-');
  if (!m || d.length === 9) return d; // année seule ou saison « AAAA-AAAA »
  return day ? `${Number(day)} ${MONTHS[Number(m) - 1]} ${y}` : `${MONTHS[Number(m) - 1]} ${y}`;
};
const periodLabel = (c: ClubItem) => {
  if (!c.first) return null;
  if (c.archives) return c.first === c.last ? `Retrouvé dans les archives en ${c.first}` : `Retrouvé dans les archives de ${c.first} à ${c.last}`;
  return c.first === c.last ? `Affilié en ${c.first}` : `Affilié de ${c.first} à ${c.last}`;
};

// --- Palmarès
const palmares = ref<Competition[]>([]);
const palmIndex = ref(0);
const palm = computed(() => palmares.value[palmIndex.value]);

// --- Carte
const joMap = ref<JoMap | null>(null);
const fedMap = ref<FederalMap | null>(null);
const mapMode = ref<'asso' | 'fed'>('asso');
const year = ref(2026);
const seasonIndex = ref(0);
const hovered = ref<{ code: string; name: string } | null>(null);
const BINS: [number, string][] = [[0, 'aucune'], [1, '1 à 4'], [5, '5 à 14'], [15, '15 à 39'], [40, '40 à 99'], [100, '100 et plus']];
const LIGUE_SLOT: Record<string, number> = { SDE: 1, SDO: 2, AQU: 3, BRE: 4, PDL: 5, NRD: 6, EST: 7, FFD: 8 };
const fedSeasons = computed(() => Object.keys(fedMap.value?.seasons ?? {}).sort());
const currentSeason = computed(() => fedSeasons.value[seasonIndex.value]);
const countUpTo = (code: string, y: number) =>
  Object.entries(joMap.value?.cum[code] ?? {}).reduce((s, [yy, n]) => s + (Number(yy) <= y ? n : 0), 0);
const binOf = (v: number) => BINS.reduce((k, b, i) => (v >= b[0] ? i : k), 0);
const topComite = (cnt: Record<string, number>) => Object.entries(cnt).sort((a, b) => b[1] - a[1])[0][0];
const fill = (code: string) => {
  if (mapMode.value === 'asso') return `var(--m${binOf(countUpTo(code, year.value))})`;
  const cnt = fedMap.value?.seasons[currentSeason.value]?.[code];
  if (!cnt) return 'var(--m0)';
  const c = topComite(cnt);
  return `var(--l${LIGUE_SLOT[fedMap.value!.ligue_of[currentSeason.value][c]] ?? 8})`;
};
const totalAsso = computed(() => Object.keys(joMap.value?.cum ?? {}).reduce((s, c) => s + countUpTo(c, year.value), 0));
const comitesShown = computed(() => {
  const data = fedMap.value?.seasons[currentSeason.value] ?? {};
  return new Set(Object.values(data).map(topComite)).size;
});
const hoverInfo = computed(() => {
  if (!hovered.value) return null;
  const { code, name } = hovered.value;
  if (mapMode.value === 'asso') {
    const n = countUpTo(code, year.value);
    return { name, lines: [`${n} association${n > 1 ? 's' : ''} créée${n > 1 ? 's' : ''} jusqu'en ${year.value}`] };
  }
  const cnt = fedMap.value?.seasons[currentSeason.value]?.[code];
  if (!cnt) return { name, lines: [`aucun club affilié connu en ${currentSeason.value}`] };
  return {
    name,
    lines: Object.entries(cnt).sort((a, b) => b[1] - a[1]).map(([c, n]) =>
      `Comité ${fedMap.value!.comites[c] ?? c} (${c}) : ${n} club${n > 1 ? 's' : ''}, Ligue ${fedMap.value!.ligues[fedMap.value!.ligue_of[currentSeason.value][c]] ?? ''}`),
  };
});
// Libellés des Comités : centre des départements qu'ils colorent (calculé à l'affichage).
const svgRef = ref<SVGSVGElement | null>(null);
const labels = ref<{ code: string; x: number; y: number }[]>([]);
const computeLabels = () => {
  labels.value = [];
  if (mapMode.value !== 'fed' || !svgRef.value || !fedMap.value) return;
  const data = fedMap.value.seasons[currentSeason.value] ?? {};
  const centers: Record<string, [number, number][]> = {};
  svgRef.value.querySelectorAll<SVGPathElement>('path[data-code]').forEach(p => {
    const cnt = data[p.dataset.code!];
    if (!cnt) return;
    const b = p.getBBox();
    (centers[topComite(cnt)] ??= []).push([b.x + b.width / 2, b.y + b.height / 2]);
  });
  labels.value = Object.entries(centers).map(([code, pts]) => ({
    code, x: pts.reduce((a, p) => a + p[0], 0) / pts.length, y: pts.reduce((a, p) => a + p[1], 0) / pts.length,
  }));
};
watch([mapMode, seasonIndex, fedMap], () => requestAnimationFrame(computeLabels));
const playing = ref<number | null>(null);
const stop = () => { if (playing.value) { clearInterval(playing.value); playing.value = null; } };
const togglePlay = () => {
  if (playing.value) { stop(); return; }
  if (mapMode.value === 'asso') {
    if (year.value >= 2026) year.value = 1980;
    playing.value = window.setInterval(() => { year.value += 1; if (year.value >= 2026) stop(); }, 350);
  } else {
    if (seasonIndex.value >= fedSeasons.value.length - 1) seasonIndex.value = 0;
    playing.value = window.setInterval(() => {
      seasonIndex.value += 1;
      if (seasonIndex.value >= fedSeasons.value.length - 1) stop();
    }, 1100);
  }
};
const setMode = (m: 'asso' | 'fed') => { stop(); mapMode.value = m; seasonIndex.value = Math.max(fedSeasons.value.length - 1, 0); };

// --- Graphiques (barres SVG, une seule série)
const barChart = (rows: [string, number | null][], labelEvery: number, height: number) => {
  const W = 960, H = height, L = 34, B = 26, T = 18;
  const max = Math.max(...rows.map(r => r[1] || 0));
  const p = Math.pow(10, Math.floor(Math.log10(max)));
  const step = max / p > 5 ? p : p / 2;
  const top = Math.ceil(max / step) * step;
  const bw = (W - L) / rows.length;
  const y = (v: number) => T + (H - T - B) * (1 - v / top);
  const grid = [];
  for (let v = 0; v <= top; v += step) grid.push({ v, y: y(v) });
  const bars = rows.map((r, i) => {
    const x = L + i * bw, w = Math.max(bw - 2, 1), v = r[1];
    if (v == null) return { i, x, bw, label: r[0], v, axis: i % labelEvery === 0 || i === rows.length - 1 };
    const yy = y(v), h = H - B - yy, rr = Math.min(4, w / 2, h);
    const d = `M${x + 1},${H - B} V${yy + rr} q0,-${rr} ${rr},-${rr} H${x + 1 + w - rr} q${rr},0 ${rr},${rr} V${H - B} Z`;
    return { i, x, bw, label: r[0], v, d, yy, axis: i % labelEvery === 0 || i === rows.length - 1 };
  });
  return { W, H, L, B, T, grid, bars };
};
const joChart = computed(() => barChart(JO_CREATIONS, 5, 220));
const clubsChart = computed(() => barChart(CLUBS_PER_SEASON, 1, 200));
const fmt = (n: number) => n.toLocaleString('fr-FR');

// --- Contribuer
const form = ref({ name: '', email: '', topic: 'Une liste de licenciés', period: '', message: '' });
const files = ref<File[]>([]);
const sending = ref(false);
const sent = ref('');
const formError = ref('');
const onFiles = (e: Event) => { files.value = Array.from((e.target as HTMLInputElement).files ?? []); };
const submit = async () => {
  formError.value = '';
  if (!form.value.email.includes('@') || !form.value.message.trim()) {
    formError.value = 'Indiquez votre e-mail et votre message.';
    return;
  }
  const fd = new FormData();
  Object.entries(form.value).forEach(([k, v]) => fd.append(k, v));
  files.value.forEach(f => fd.append('files', f));
  sending.value = true;
  try {
    const { data } = await dartsHubApi.post('/history/contributions', fd);
    sent.value = data.detail;
    form.value = { name: '', email: '', topic: 'Une liste de licenciés', period: '', message: '' };
    files.value = [];
  } catch (e: any) {
    formError.value = e?.response?.data?.detail || "L'envoi a échoué, réessayez plus tard.";
  } finally {
    sending.value = false;
  }
};

onMounted(async () => {
  const [ev, pa, doy, fed, old] = await Promise.allSettled([
    dartsHubApi.get('/history/events'), dartsHubApi.get('/history/palmares'),
    dartsHubApi.get('/history/doyens', { params: { limit: 30 } }), dartsHubApi.get('/history/federal-map'),
    dartsHubApi.get('/history/clubs'),
  ]);
  if (old.status === 'fulfilled') clubs.value = old.value.data;
  if (ev.status === 'fulfilled') events.value = ev.value.data;
  if (pa.status === 'fulfilled') palmares.value = pa.value.data;
  if (doy.status === 'fulfilled') doyens.value = doy.value.data;
  if (fed.status === 'fulfilled') { fedMap.value = fed.value.data; seasonIndex.value = Math.max(fedSeasons.value.length - 1, 0); }
  try { joMap.value = await (await fetch('/histoire-map.json')).json(); } catch { joMap.value = null; }
});
</script>

<template>
  <div class="hp">
    <div class="topbar"><div class="in">
      <RouterLink to="/" class="brand-link">
        <img class="logo l" :src="logoLight" alt="" />
        <img class="logo d" :src="logoDark" alt="" />
        <span class="brand">DartsHub</span>
      </RouterLink>
      <span class="crumb">/ Histoire</span>
      <a class="top-cta" href="#contribuer" @click.prevent="go('contribuer')">Contribuer</a>
    </div></div>

    <div class="page">
      <div class="page-header">
        <div>
          <h1>Histoire des fléchettes en France</h1>
          <p class="sub">Fédérations, Ligues, Comités et clubs : comment l'organisation du jeu s'est construite, d'après le Journal officiel des associations, les documents fédéraux et vos souvenirs.</p>
        </div>
      </div>

      <div class="appeal">
        <div>
          <h2>Vous étiez là avant internet ? Cette histoire s'écrit avec vous</h2>
          <p>Anciens joueurs, dirigeants, secrétaires de club ou de Comité : vous gardez la mémoire d'une époque dont il reste peu de traces. Une vieille licence, une liste de clubs, un classement ou un simple souvenir peuvent combler les trous. Chaque contribution est vérifiée avant d'être publiée.</p>
        </div>
        <a class="btn" href="#contribuer" @click.prevent="go('contribuer')">Partager un document</a>
      </div>

      <p class="caveat"><span><b>Une reconstitution, pas une vérité figée.</b> Ces pages croisent des sources parfois incomplètes ou contradictoires : des erreurs sont possibles. Une date, un nom ou une commune vous semble faux ? <a href="#contribuer" @click.prevent="go('contribuer')">Signalez-le</a>, toute correction documentée est la bienvenue.</span></p>

      <div class="stats">
        <div class="stat"><b>2 061</b><span>associations de fléchettes déclarées au Journal officiel depuis 1922, affiliées ou non à une Fédération</span></div>
        <div class="stat"><b>1976</b><span>fondation de la Fédération française de darts, le 18 mai, d'après ses statuts</span></div>
        <div class="stat"><b>~233</b><span>clubs affiliés à la FFD en 2025-2026 (estimation)</span></div>
        <div class="stat"><b>8 · 29</b><span>Ligues et Comités en 2026-2027</span></div>
      </div>

      <nav class="tab-switch" aria-label="Rubriques">
        <a v-for="v in VIEWS" :key="v.id" :href="`#${v.id}`" :aria-current="view === v.id ? 'page' : undefined" @click.prevent="go(v.id)">{{ v.label }}</a>
      </nav>

      <!-- Frise -->
      <section v-if="view === 'frise'">
        <div class="page-header tight">
          <p class="sub">Dates de parution au Journal officiel : création, changement de nom ou dissolution.</p>
          <div class="filters">
            <div class="picker"><label for="h-kind">Type</label>
              <select id="h-kind" v-model="kindFilter">
                <option value="all">Tous</option><option value="federation">Fédérations</option><option value="ligue">Ligues</option>
                <option value="comite">Comités</option><option value="club">Clubs</option>
              </select></div>
            <div class="picker"><label for="h-region">Région</label>
              <select id="h-region" v-model="regionFilter"><option v-for="[v, l] in REGIONS" :key="v" :value="v">{{ l }}</option></select></div>
          </div>
        </div>
        <p v-if="!groupedEvents.length" class="empty">Aucun événement pour ce filtre.</p>
        <section v-for="[decade, evs] in groupedEvents" :key="decade" class="group">
          <h2 class="group-header">Années {{ decade }}</h2>
          <div class="list">
            <article v-for="e in evs" :key="e.id" class="row">
              <div class="badge"><span class="y">{{ e.date.slice(0, 4) }}</span><span v-if="e.date.slice(5) !== '00'" class="m">{{ MONTHS[Number(e.date.slice(5)) - 1] }}</span></div>
              <div class="body"><h3 class="name">{{ e.title }}</h3><p v-if="e.detail" class="meta">{{ e.detail }}</p></div>
              <span class="chip" :class="e.kind">{{ KIND_LABEL[e.kind] }}</span>
            </article>
          </div>
        </section>
      </section>

      <!-- Carte -->
      <section v-if="view === 'carte'">
        <div class="card">
          <div class="toggle" role="group" aria-label="Couche de la carte">
            <button type="button" :aria-pressed="mapMode === 'asso'" @click="setMode('asso')">Associations</button>
            <button type="button" :aria-pressed="mapMode === 'fed'" @click="setMode('fed')">Découpage fédéral</button>
          </div>
          <template v-if="mapMode === 'asso'">
            <h3>Les associations de fléchettes, département par département</h3>
            <p class="note">Nombre cumulé d'associations créées dans chaque département jusqu'à l'année choisie, d'après le Journal officiel, affiliées ou non à une Fédération.</p>
          </template>
          <template v-else>
            <h3>Le découpage fédéral, saison par saison</h3>
            <p class="note">Chaque département prend la couleur de la Ligue du Comité qui y compte le plus de clubs affiliés connus. Estimation à partir des clubs retrouvés ; avant 2017-2018, seules les sources du Sud-Est sont connues.</p>
          </template>
          <div class="slider">
            <button class="play" type="button" @click="togglePlay">{{ playing ? '❚❚ Pause' : '▶ Lecture' }}</button>
            <input v-if="mapMode === 'asso'" v-model.number="year" type="range" min="1980" max="2026" step="1" aria-label="Année">
            <input v-else v-model.number="seasonIndex" type="range" min="0" :max="Math.max(fedSeasons.length - 1, 0)" step="1" aria-label="Saison">
          </div>
          <div class="ticks">
            <template v-if="mapMode === 'asso'"><span v-for="t in ['1980', '1990', '2000', '2010', '2020', '2026']" :key="t">{{ t }}</span></template>
            <template v-else><span>{{ fedSeasons[0] }}</span><span>{{ fedSeasons[Math.floor(fedSeasons.length / 2)] }}</span><span>{{ fedSeasons[fedSeasons.length - 1] }}</span></template>
          </div>
          <div class="mapwrap">
            <div class="map">
              <p v-if="!joMap" class="empty">Chargement de la carte…</p>
              <svg v-else ref="svgRef" viewBox="0 0 600 580" role="img" aria-label="Carte des départements">
                <rect class="inset" x="8" y="452" width="82" height="86" rx="6" />
                <text class="inset-label" x="14" y="532">La Réunion</text>
                <path v-for="[code, name, d] in joMap.paths" :key="code" :d="d" :data-code="code" :style="{ fill: fill(code) }"
                  @mouseenter="hovered = { code, name }" @mouseleave="hovered = null" />
                <text v-for="l in labels" :key="l.code" class="clabel" :x="l.x" :y="l.y + 4" text-anchor="middle">{{ l.code }}</text>
              </svg>
            </div>
            <div class="side">
              <div class="year-big">{{ mapMode === 'asso' ? year : currentSeason }}</div>
              <p v-if="mapMode === 'asso'" class="total"><b>{{ fmt(totalAsso) }}</b> associations créées depuis 1980</p>
              <p v-else class="total"><b>{{ comitesShown }}</b> Comité{{ comitesShown > 1 ? 's' : '' }} avec au moins un club situé</p>
              <div class="hover-info">
                <template v-if="hoverInfo"><b>{{ hoverInfo.name }}</b><br><span v-for="(l, i) in hoverInfo.lines" :key="i">{{ l }}<br></span></template>
                <template v-else>Survolez un département.</template>
              </div>
              <div class="scale">
                <template v-if="mapMode === 'asso'"><div v-for="(b, i) in BINS" :key="i"><i :style="{ background: `var(--m${i})` }"></i>{{ b[1] }}</div></template>
                <template v-else>
                  <div v-for="(slot, code) in LIGUE_SLOT" :key="code"><i :style="{ background: `var(--l${slot})` }"></i>{{ fedMap?.ligues[code] ?? code }}</div>
                  <div><i style="background: var(--m0)"></i>aucun club connu</div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Arbres -->
      <section v-if="view === 'arbres'">
        <p class="sub spaced">Qui est devenu quoi. Une case en pointillés est un Comité ou une Ligue disparu ; une case dorée est une Ligue.</p>
        <article v-for="t in TREES" :key="t.title" class="tree">
          <h3>{{ t.title }}</h3>
          <p class="note">{{ t.note }}</p>
          <div class="flow">
            <div v-for="s in t.stages" :key="s.era" class="stage">
              <div class="era">{{ s.era }}</div>
              <div v-for="([code, label, gone, lig], i) in s.nodes" :key="i" class="node" :class="{ gone, lig }">
                <b v-if="code">{{ code }}</b><span class="s">{{ label }}</span>
              </div>
            </div>
          </div>
        </article>
      </section>

      <!-- Doyens -->
      <section v-if="view === 'doyens'">
        <p class="sub spaced">Les plus anciens clubs encore affiliés, classés par date de création de leur association, d'après le Répertoire national des associations et le Journal officiel. Votre club manque ou sa date est fausse ? <a href="#contribuer" @click.prevent="go('contribuer')">Dites-le-nous</a>.</p>
        <div class="doyens">
          <article v-for="(d, i) in doyens" :key="d.name" class="doyen">
            <span class="rank">{{ i + 1 }}</span>
            <div class="lg"><img v-if="d.logo" :src="logoUrl(d.logo)" alt="" loading="lazy"><template v-else>{{ initials(d.name) }}</template></div>
            <div class="tx"><b>{{ d.name }}</b><span>{{ [d.commune, d.comite ? `Comité ${d.comite}` : null].filter(Boolean).join(' · ') }}</span><span class="when">{{ foundedLabel(d) }}</span></div>
          </article>
        </div>
      </section>

      <!-- Les clubs -->
      <section v-if="view === 'clubs'">
        <div class="page-header tight">
          <p class="sub">Tous les clubs connus, Comité par Comité : ceux affiliés aujourd'hui à la Fédération et ceux qui ne le sont plus, retrouvés dans la base de la Fédération, le Journal officiel et les archives des anciens sites. Votre club manque ou une information est fausse ? <a href="#contribuer" @click.prevent="go('contribuer')">Dites-le-nous</a>.</p>
          <div class="filters">
            <div class="picker"><label for="h-cl-region">Région</label>
              <select id="h-cl-region" v-model="clubRegion"><option v-for="[v, l] in REGIONS" :key="v" :value="v">{{ l }}</option><option value="autre">Hors Comité</option></select></div>
            <div class="picker"><label for="h-cl-comite">Comité</label>
              <select id="h-cl-comite" v-model="clubComite"><option value="all">Tous</option><option v-for="c in clubComites" :key="c" :value="c">{{ c }}</option></select></div>
            <div class="picker"><label for="h-cl-status">Statut</label>
              <select id="h-cl-status" v-model="clubStatus"><option value="all">Tous</option><option value="actif">Affiliés</option><option value="ancien">Non affiliés ou dissous</option></select></div>
          </div>
        </div>
        <p v-if="!clubGroups.length" class="empty">Aucun club pour ce filtre.</p>
        <section v-for="[comite, list] in clubGroups" :key="comite" class="group">
          <h2 class="group-header">{{ comite }} · {{ list.length }} club{{ list.length > 1 ? 's' : '' }}</h2>
          <div class="old-clubs">
            <article v-for="c in list" :key="c.name + c.first" class="old-club" :class="c.status">
              <div class="oc-head"><b>{{ c.name }}</b><span class="status" :class="c.status">{{ STATUS_LABEL[c.status] }}<template v-if="c.dissolved"> en {{ c.dissolved.slice(0, 4) }}</template></span></div>
              <span v-if="c.commune" class="oc-line">{{ c.commune }}</span>
              <span v-if="c.founded" class="oc-line">Association créée {{ c.founded_note ? `en ${c.founded.slice(0, 4)} ${c.founded_note}` : `le ${fmtDate(c.founded)}` }}</span>
              <span v-if="periodLabel(c)" class="oc-line when">{{ periodLabel(c) }}</span>
              <span v-if="c.note" class="oc-note">{{ c.note }}</span>
              <details v-if="c.history.length > 2" class="oc-hist">
                <summary>Historique</summary>
                <ul><li v-for="(h, i) in c.history" :key="i"><b>{{ fmtDate(h.date) }}</b> · {{ h.text }}</li></ul>
              </details>
            </article>
          </div>
        </section>
      </section>

      <!-- Palmarès -->
      <section v-if="view === 'palmares'">
        <div class="page-header tight">
          <p class="sub">{{ palm?.note }}</p>
          <div class="filters"><div class="picker"><label for="h-palm">Épreuve</label>
            <select id="h-palm" v-model.number="palmIndex"><option v-for="(c, i) in palmares" :key="c.id" :value="i">{{ c.title }}</option></select></div></div>
        </div>
        <div v-if="palm" class="card flush">
          <table class="palm">
            <thead><tr><th>Saison</th><th v-for="c in palm.columns" :key="c">{{ c }}</th></tr></thead>
            <tbody>
              <tr v-for="r in [...palm.rows].reverse()" :key="r[0]!">
                <td>{{ r[0] }}</td>
                <td v-for="(v, i) in r.slice(1)" :key="i" :class="{ nd: !v, todo: v === '?' }">{{ v === '?' ? 'à compléter' : v || 'non disputé' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- En chiffres -->
      <section v-if="view === 'chiffres'">
        <div class="card">
          <h3>Associations de fléchettes créées chaque année</h3>
          <p class="note">Déclarations de création au Journal officiel dont le nom ou l'objet parle de fléchettes ou de darts, hors fléchettes électroniques. Une association n'est pas forcément un club affilié à la FFD : on y trouve aussi des équipes de bar, des associations de loisir ou des clubs restés indépendants. Les années 1922 à 1979 sont regroupées.</p>
          <svg class="chart dense" :viewBox="`0 0 ${joChart.W} ${joChart.H}`" role="img" aria-label="Associations créées par année">
            <g v-for="g in joChart.grid" :key="g.v"><line class="grid" :x1="joChart.L" :x2="joChart.W" :y1="g.y" :y2="g.y" /><text class="axis" :x="joChart.L - 6" :y="g.y + 4" text-anchor="end">{{ g.v }}</text></g>
            <g v-for="b in joChart.bars" :key="b.i">
              <template v-if="b.v != null"><path class="bar" :d="b.d"><title>{{ b.label }} : {{ b.v }} créations</title></path>
                <text class="lbl" :x="b.x + b.bw / 2" :y="b.yy! - 4" text-anchor="middle">{{ b.v }}</text></template>
              <text v-if="b.axis" class="axis" :x="b.x + b.bw / 2" :y="joChart.H - 8" text-anchor="middle">{{ b.label }}</text>
            </g>
          </svg>
        </div>
        <div class="card">
          <h3>Clubs affiliés à la FFD par saison (estimation)</h3>
          <p class="note">Estimation à partir des classements, résultats et listes conservés : un club compte dès qu'on lui connaît au moins un licencié dans la saison. Aucune source pour 2020-2021, saison interrompue par la crise sanitaire. 2026-2027 est en cours.</p>
          <svg class="chart" :viewBox="`0 0 ${clubsChart.W} ${clubsChart.H}`" role="img" aria-label="Clubs affiliés par saison">
            <g v-for="g in clubsChart.grid" :key="g.v"><line class="grid" :x1="clubsChart.L" :x2="clubsChart.W" :y1="g.y" :y2="g.y" /><text class="axis" :x="clubsChart.L - 6" :y="g.y + 4" text-anchor="end">{{ g.v }}</text></g>
            <g v-for="b in clubsChart.bars" :key="b.i">
              <template v-if="b.v != null"><path class="bar" :d="b.d"><title>{{ b.label }} : environ {{ b.v }} clubs</title></path>
                <text class="lbl" :x="b.x + b.bw / 2" :y="b.yy! - 4" text-anchor="middle">{{ b.v }}</text></template>
              <text v-else class="ann" :x="b.x + b.bw / 2" :y="clubsChart.H - clubsChart.B - 8" text-anchor="middle">aucune source</text>
              <text class="axis" :x="b.x + b.bw / 2" :y="clubsChart.H - 8" text-anchor="middle">{{ b.label }}</text>
            </g>
          </svg>
        </div>
        <div class="card">
          <h3>Les départements où l'on a créé le plus d'associations</h3>
          <p class="note">Même source que le premier graphique, de 1922 à 2026, affiliées ou non. La Bretagne et le Nord dominent depuis les années 1980.</p>
          <div class="hbars">
            <div v-for="[n, v] in TOP_DEPARTMENTS" :key="n" class="hb"><span>{{ n }}</span>
              <div class="track"><div class="fill" :style="{ width: `${(v / TOP_DEPARTMENTS[0][1]) * 100}%` }"></div></div><span class="v">{{ v }}</span></div>
          </div>
        </div>
      </section>

      <!-- Contribuer -->
      <section v-if="view === 'contribuer'" class="contrib">
        <div class="card">
          <h3>Ce qui nous manque</h3>
          <p class="note">Tout document d'époque aide, même incomplet : une photo prise au téléphone ou un tableur suffit.</p>
          <div class="wanted">
            <div><b>Listes de licenciés</b><span>Listings de clubs ou de Comités, même partiels, et nombre de licenciés par saison.</span></div>
            <div><b>Les Comités et les Ligues</b><span>Créations, changements de nom, fusions et disparitions, surtout avant 2014.</span></div>
            <div><b>Les clubs disparus</b><span>Leur nom exact, leur commune, leur Comité et leurs années d'activité.</span></div>
            <div><b>Saison 2020-2021</b><span>Aucune trace pour l'instant : clubs affiliés, compétitions maintenues ou annulées.</span></div>
            <div><b>Les palmarès</b><span>Résultats des championnats de France, de Ligue et de Comité, en particulier depuis 2022.</span></div>
          </div>
        </div>
        <div class="card">
          <h3>Partager un document ou un souvenir</h3>
          <p class="note">Nous vous recontactons si besoin avant toute publication.</p>
          <form @submit.prevent="submit">
            <div class="two">
              <div class="field"><label for="f-name">Votre nom (facultatif)</label><input id="f-name" v-model="form.name" type="text" autocomplete="name"></div>
              <div class="field"><label for="f-mail">E-mail pour vous répondre</label><input id="f-mail" v-model="form.email" type="email" autocomplete="email" required></div>
            </div>
            <div class="two">
              <div class="field"><label for="f-topic">Sujet</label>
                <select id="f-topic" v-model="form.topic">
                  <option>Une liste de licenciés</option><option>Un club</option><option>Un Comité ou une Ligue</option>
                  <option>Des résultats ou un palmarès</option><option>Une erreur sur cette page</option><option>Autre</option>
                </select></div>
              <div class="field"><label for="f-period">Période concernée</label><input id="f-period" v-model="form.period" type="text" placeholder="ex. 1995-2002"></div>
            </div>
            <div class="field"><label for="f-msg">Votre message</label><textarea id="f-msg" v-model="form.message" placeholder="Nom du club, commune, années, ce dont vous vous souvenez…"></textarea></div>
            <div class="field"><label for="f-files">Documents (PDF, tableurs, photos de documents ; 5 fichiers, 10 Mo chacun)</label>
              <input id="f-files" type="file" multiple accept="image/*,application/pdf,.xls,.xlsx,.csv,.ods,.doc,.docx,.txt" @change="onFiles"></div>
            <p class="privacy">Vos coordonnées servent uniquement à vous répondre et ne sont jamais publiées. Sur les documents publiés, seuls les noms de clubs, de Comités et de Ligues, les communes et les résultats apparaissent.</p>
            <p v-if="formError" class="error">{{ formError }}</p>
            <div><button class="btn" type="submit" :disabled="sending">{{ sending ? 'Envoi…' : 'Envoyer' }}</button></div>
            <div v-if="sent" class="sent" role="status">{{ sent }}</div>
          </form>
        </div>
      </section>

      <footer>
        Sources :
        <ul>
          <li>Journal officiel de la République française, annonces des associations, et Répertoire national des associations (données ouvertes).</li>
          <li>Documents de la Fédération française de darts : classements, résultats, listes d'inscription et calendriers, 2014-2026.</li>
          <li>Fédération Sud-Est de fléchettes (2002-2014) et ancien site de la FFD (années 2000).</li>
          <li>Contours des départements : france-geojson, d'après l'IGN.</li>
        </ul>
      </footer>
    </div>
  </div>
</template>

<style scoped>
/* DA du calendrier DartsHub (Compet, thèmes calendrier-light / calendrier-dark). */
.hp {
  --bg: #ffffff; --surface: #f3f5f8; --line: #d9dee7; --fg: #1a2230; --muted: #5b6476;
  --accent: #a86400; --accent-soft: rgba(168, 100, 0, .12); --radius: 12px;
  --m0: #eef0f4; --m1: #f7e3bf; --m2: #efc277; --m3: #d9962a; --m4: #a86400; --m5: #6b3f00;
  --l1: #2a78d6; --l2: #eb6834; --l3: #1baf7a; --l4: #eda100; --l5: #e87ba4; --l6: #008300; --l7: #6250d6; --l8: #e34948;
  --logo-light: block; --logo-dark: none;
  min-height: 100vh; background: var(--bg); color: var(--fg); font: 15px/1.55 'Inter', system-ui, sans-serif;
}
@media (prefers-color-scheme: dark) {
  .hp {
    --bg: #0c1016; --surface: #161b24; --line: #2a3140; --fg: #e7ebf1; --muted: #8b93a3;
    --accent: #f5a524; --accent-soft: rgba(245, 165, 36, .12);
    --m0: #1f2530; --m1: #4a3a1c; --m2: #7d5a1c; --m3: #b37a1c; --m4: #f5a524; --m5: #ffd88a;
    --l1: #3987e5; --l2: #d95926; --l3: #199e70; --l4: #c98500; --l5: #d55181; --l6: #008300; --l7: #9085e9; --l8: #e66767;
    --logo-light: none; --logo-dark: block;
  }
}
.hp * { box-sizing: border-box; }
.hp a { color: var(--accent); text-decoration: none; }
.hp a:hover { text-decoration: underline; }
.topbar { background: var(--surface); border-bottom: 1px solid var(--line); }
.topbar .in { max-width: 1100px; margin: 0 auto; padding: 10px 16px; display: flex; align-items: center; gap: 12px; }
.brand-link { display: flex; align-items: center; gap: 10px; color: var(--fg) !important; }
.brand-link:hover { text-decoration: none !important; }
.logo { height: 38px; width: auto; }
.logo.l { display: var(--logo-light); }
.logo.d { display: var(--logo-dark); }
.brand { font-weight: 800; font-size: 18px; }
.crumb { color: var(--muted); font-size: 14px; }
.top-cta { margin-left: auto; font-size: .85rem; font-weight: 700; color: #0c1016 !important; background: var(--accent); padding: 8px 14px; border-radius: 999px; white-space: nowrap; }
.page { max-width: 1100px; margin: 0 auto; padding: 8px 16px 48px; }
.page-header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 16px; margin: 24px 0 20px; }
.page-header.tight { margin-top: 0; }
/* Le h1 global de DartsHub (couleur et soulignement dégradé) ne s'applique pas ici. */
.hp h1 { font-size: 1.6rem; font-weight: 800; margin: 0 0 4px; color: var(--fg); background: none; -webkit-text-fill-color: currentColor; }
.hp h1::after, .hp h1::before { display: none; }
.sub { color: var(--muted); margin: 0; font-size: .9rem; max-width: 64ch; }
.sub.spaced { margin-bottom: 18px; }
.appeal { display: flex; gap: 18px; align-items: center; flex-wrap: wrap; background: var(--accent-soft); border: 1px solid var(--accent); border-radius: var(--radius); padding: 16px 18px; margin-bottom: 22px; }
.appeal div { flex: 1 1 320px; }
.appeal h2 { font-size: 1.05rem; font-weight: 800; margin: 0 0 4px; }
.appeal p { margin: 0; font-size: .9rem; }
.btn { display: inline-block; border: 0; cursor: pointer; font: inherit; font-weight: 700; font-size: .9rem; color: #0c1016 !important; background: var(--accent); padding: 10px 18px; border-radius: 999px; }
.btn:disabled { opacity: .6; cursor: default; }
.caveat { font-size: .85rem; color: var(--muted); border: 1px dashed var(--line); border-radius: var(--radius); padding: 10px 14px; margin: 0 0 22px; }
.caveat b { color: var(--fg); }
.stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 12px; margin-bottom: 22px; }
.stat { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 14px 16px; }
.stat b { display: block; font-size: 1.7rem; font-weight: 800; line-height: 1.1; }
.stat span { font-size: .82rem; color: var(--muted); }
.tab-switch, .toggle { display: inline-flex; background: var(--surface); border: 1px solid var(--line); border-radius: 999px; padding: 4px; margin-bottom: 26px; flex-wrap: wrap; }
.toggle { background: var(--bg); margin-bottom: 12px; }
.tab-switch a, .toggle button { color: var(--muted) !important; font-weight: 700; font-size: .9rem; padding: 8px 16px; border-radius: 999px; border: 0; background: none; cursor: pointer; font-family: inherit; }
.tab-switch a:hover { text-decoration: none; }
.tab-switch a[aria-current='page'], .toggle button[aria-pressed='true'] { background: var(--accent); color: #0c1016 !important; }
.filters { display: flex; flex-wrap: wrap; gap: 10px 18px; align-items: center; }
.picker { display: flex; align-items: center; gap: 8px; }
.picker label { font-size: .85rem; color: var(--muted); font-weight: 600; }
.hp select, .hp input[type='text'], .hp input[type='email'], .hp textarea { font: inherit; font-size: .9rem; padding: 8px 14px; border-radius: 999px; border: 1px solid var(--line); background: var(--surface); color: var(--fg); }
.group { margin-bottom: 30px; }
.group-header { color: var(--accent); font-size: .85rem; font-weight: 800; text-transform: uppercase; letter-spacing: .08em; margin: 0 0 12px; padding-bottom: 8px; border-bottom: 1px solid var(--line); }
.list { display: flex; flex-direction: column; gap: 10px; }
.row { display: flex; align-items: center; gap: 16px; background: var(--surface); border: 1px solid var(--line); border-left: 3px solid transparent; border-radius: var(--radius); padding: 12px 16px; transition: border-color .15s, transform .15s; }
.row:hover { transform: translateY(-2px); border-left-color: var(--accent); }
.badge { flex: 0 0 auto; min-width: 62px; display: flex; flex-direction: column; align-items: center; background: var(--bg); border-radius: var(--radius); padding: 7px 6px; }
.badge .y { font-size: 1.15rem; font-weight: 800; line-height: 1; }
.badge .m { font-size: .65rem; color: var(--muted); font-weight: 700; text-transform: uppercase; margin-top: 3px; }
.body { flex: 1; min-width: 0; }
.name { margin: 0 0 3px; font-size: 1rem; font-weight: 700; }
.meta { margin: 0; font-size: .85rem; color: var(--muted); }
.chip { flex: 0 0 auto; font-size: .78rem; font-weight: 700; padding: 4px 11px; border-radius: var(--radius); color: var(--muted); border: 1px solid var(--line); }
.chip.federation { color: var(--accent); background: var(--accent-soft); border-color: transparent; }
.chip.ligue { color: var(--fg); border-color: var(--fg); }
.empty { color: var(--muted); font-style: italic; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 18px; margin-bottom: 16px; }
.card.flush { padding: 0; overflow-x: auto; }
.card h3 { font-size: 1.05rem; font-weight: 800; margin: 0 0 2px; }
.note { margin: 0 0 10px; color: var(--muted); font-size: .88rem; max-width: 72ch; }
.tree { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 18px; margin-bottom: 16px; }
.tree h3 { font-size: 1.1rem; font-weight: 800; margin: 0 0 4px; }
.flow { display: flex; overflow-x: auto; padding-bottom: 6px; }
.stage { min-width: 165px; flex: 1 0 165px; display: grid; align-content: start; gap: 8px; position: relative; padding-right: 28px; }
.stage:not(:last-child)::after { content: ''; position: absolute; right: 9px; top: 6px; width: 9px; height: 9px; border-top: 2px solid var(--accent); border-right: 2px solid var(--accent); transform: rotate(45deg); }
.era { font-size: .75rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: var(--accent); padding-bottom: 6px; border-bottom: 1px solid var(--line); }
.node { border: 1px solid var(--line); border-radius: 8px; padding: 8px 10px; background: var(--bg); }
.node b { font-weight: 800; margin-right: 4px; }
.node.gone { border-style: dashed; opacity: .75; }
.node.lig { border-color: var(--accent); }
.node .s { display: block; font-size: .8rem; color: var(--muted); margin-top: 2px; line-height: 1.35; }
.mapwrap { display: grid; grid-template-columns: minmax(0, 1fr) 270px; gap: 18px; align-items: start; margin-top: 14px; }
.map svg { width: 100%; height: auto; display: block; max-height: 640px; }
.map path { stroke: var(--bg); stroke-width: .8; transition: fill .25s; }
.map path:hover { stroke: var(--fg); stroke-width: 1.6; }
.map .inset { fill: none; stroke: var(--line); stroke-dasharray: 3 3; }
.map .inset-label { fill: var(--muted); font-size: 10px; font-weight: 600; }
.map .clabel { fill: #fff; font-size: 11px; font-weight: 800; paint-order: stroke; stroke: rgba(0, 0, 0, .45); stroke-width: 2.5px; pointer-events: none; }
.year-big { font-size: 3rem; font-weight: 800; line-height: 1; color: var(--accent); }
.side p { margin: 6px 0 14px; color: var(--muted); font-size: .88rem; }
.side .total b { font-size: 1.4rem; font-weight: 800; color: var(--fg); }
.slider { display: flex; align-items: center; gap: 12px; margin: 6px 0 4px; }
.slider input { flex: 1; accent-color: var(--accent); }
.play { border: 1px solid var(--accent); background: var(--accent); color: #0c1016; font: inherit; font-weight: 700; font-size: .85rem; border-radius: 999px; padding: 7px 14px; cursor: pointer; min-width: 92px; }
.ticks { display: flex; justify-content: space-between; font-size: .72rem; color: var(--muted); margin-left: 104px; }
.scale { display: grid; gap: 4px; font-size: .8rem; color: var(--muted); }
.scale div { display: flex; align-items: center; gap: 8px; }
.scale i { width: 18px; height: 12px; border-radius: 3px; display: inline-block; flex: 0 0 auto; }
.hover-info { min-height: 62px; font-size: .88rem; color: var(--muted); }
.hover-info b { color: var(--fg); }
.doyens { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
.doyen { display: flex; align-items: center; gap: 14px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 12px 14px; }
.doyen .rank { flex: 0 0 auto; width: 26px; font-weight: 800; color: var(--muted); text-align: right; }
.doyen .lg { flex: 0 0 auto; width: 52px; height: 52px; border-radius: 10px; background: var(--bg); display: grid; place-items: center; overflow: hidden; font-weight: 800; color: var(--accent); }
.doyen .lg img { width: 100%; height: 100%; object-fit: contain; background: #fff; }
.doyen .tx { min-width: 0; }
.doyen .tx b { display: block; font-size: .98rem; }
.doyen .tx span { display: block; font-size: .82rem; color: var(--muted); }
.doyen .tx span.when { color: var(--accent); font-weight: 600; }
.old-clubs { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.old-club { display: flex; flex-direction: column; gap: 3px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 12px 14px; }
.oc-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.oc-head b { font-size: .98rem; }
.oc-line { font-size: .82rem; color: var(--muted); }
.oc-line.when { color: var(--accent); font-weight: 600; }
.oc-note { font-size: .8rem; color: var(--muted); font-style: italic; margin-top: 2px; }
.old-club.disparu, .old-club.dissous { opacity: .85; }
.status { flex: 0 0 auto; font-size: .72rem; font-weight: 700; padding: 3px 9px; border-radius: 999px; }
.status.actif { color: var(--accent); background: var(--accent-soft); }
.status.disparu { color: #e34948; border: 1px solid #e34948; }
.status.dissous { color: #fff; background: #e34948; }
.oc-hist { margin-top: 4px; font-size: .8rem; }
.oc-hist summary { cursor: pointer; color: var(--muted); font-weight: 600; }
.oc-hist ul { margin: 6px 0 0; padding-left: 16px; color: var(--muted); }
.oc-hist b { color: var(--fg); }
table.palm { width: 100%; border-collapse: collapse; font-size: .85rem; }
table.palm th { background: var(--surface); color: var(--accent); font-size: .75rem; text-transform: uppercase; letter-spacing: .06em; padding: 10px 12px; border-bottom: 1px solid var(--line); white-space: nowrap; text-align: left; }
table.palm td { padding: 8px 12px; border-bottom: 1px solid var(--line); vertical-align: top; color: var(--fg); background: transparent; }
table.palm td:first-child { font-weight: 800; white-space: nowrap; }
table.palm td.nd { color: var(--muted); }
table.palm td.todo { color: var(--accent); font-style: italic; }
table.palm tr:hover td { background: var(--accent-soft); }
.chart { display: block; width: 100%; height: auto; overflow: visible; }
.chart .grid { stroke: var(--line); stroke-width: 1; }
.chart .axis { fill: var(--muted); font-size: 11px; }
.chart .bar { fill: var(--accent); }
.chart .bar:hover { opacity: .75; }
.chart .lbl { fill: var(--fg); font-size: 11px; font-weight: 700; }
.chart.dense .lbl { font-size: 8.5px; font-weight: 600; }
.chart .ann { fill: var(--muted); font-size: 11px; }
.hbars { display: grid; gap: 6px; }
.hb { display: grid; grid-template-columns: 150px 1fr 40px; gap: 10px; align-items: center; font-size: .85rem; }
.hb .track { height: 12px; }
.hb .fill { height: 100%; background: var(--accent); border-radius: 0 4px 4px 0; }
.hb .v { font-weight: 700; text-align: right; }
.contrib { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr); gap: 18px; align-items: start; }
.wanted { display: grid; gap: 10px; }
.wanted div { background: var(--bg); border: 1px solid var(--line); border-radius: 10px; padding: 10px 12px; font-size: .88rem; }
.wanted b { display: block; }
.wanted span { color: var(--muted); }
form { display: grid; gap: 12px; }
.field { display: grid; gap: 5px; }
.field label { font-size: .85rem; font-weight: 600; color: var(--muted); }
.hp textarea { border-radius: 12px; min-height: 130px; resize: vertical; }
.hp input[type='file'] { font: inherit; font-size: .85rem; color: var(--muted); }
.two { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.privacy { font-size: .8rem; color: var(--muted); margin: 0; }
.error { color: #e34948; margin: 0; font-size: .88rem; }
.sent { background: var(--accent-soft); border-radius: 10px; padding: 10px 12px; font-size: .88rem; }
footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid var(--line); font-size: .82rem; color: var(--muted); }
footer ul { padding-left: 18px; margin: 6px 0 0; }
@media (max-width: 760px) {
  .mapwrap, .contrib { grid-template-columns: 1fr; }
  .ticks { margin-left: 0; }
}
@media (max-width: 600px) {
  .row { flex-wrap: wrap; }
  .chip { margin-left: 78px; }
  .hb { grid-template-columns: 110px 1fr 34px; }
  .crumb { display: none; }
  .two { grid-template-columns: 1fr; }
}
</style>
